import type { PlannerState } from '@/views/3d-planner/store/plannerState';
import { findFittedCup } from '@/views/3d-planner/api/fittedComponents';
import { type Matrix4, Vector3 } from 'three';
import { addVectors, multiplyScalar, vector3 } from '@/domain/geometry/vector3';
import type { AnatomicalOffset } from '@/domain/anatomy/pelvis/anatomicalOffset';
import { type LpsBasis } from '@/domain/anatomy/LPS';
import { matrixFromApi } from '@/domain/geometry/apiMatrix';
import { computeBearingUrl, computeLinerUrl } from '@/views/3d-planner/fitting/componentUrls';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { logValidation } from '@/views/3d-planner/operations/logValidation';
import {
    formatMatrixBasis,
    formatVector,
    indent,
    joinIndented,
} from '@/domain/geometry/formatMath';
import { Side } from '@/domain/anatomy/side';

/**
 * The offset of the cup as represented in the planner
 */
export type AcetabularCupOffset = {
    depth: number;
    ap: number;
    si: number;
};

export type AlignmentCupOffset = {
    ml: number;
    ap: number;
    si: number;
};

/**
 * A representation of the fitted up that is derived from fitted-cup data on the API.
 */
export type FittedCup = {
    /** 'Global' basis */
    basis: LpsBasis & {
        lateral: Vector3;
        inferior: Vector3;
    };

    /** The native hip-joint centre in world/CT space. */
    nativeHjc: Vector3;

    /**
     * The fitted hip-joint-centre in world/CT space.
     *
     * This is the centre-of rotation of the fitted-head inside the fitted-cup. The cup-group
     * transform is originally at this point, so the cup and liner effectively rotate around it.
     * Also, when the stem-group is retracted it is moved so its position, which corresponds to
     * the centre-of-rotation of the head, matches this point.
     *
     * This is obtained as the 'head-centre' of the fitted liner on the API. Note that the fitted
     * head on the API is in it's 'native' position: relative to the stem which is fitted to the
     * native position of the femur. This means we can't use the initial transform of the head
     * component to find the fitted hip-joint-centre.
     */
    fittedHjc: Vector3;

    /** The normal or **depth** direction of cup offset */
    normal: Vector3;

    /** The inferior direction of cup offset */
    siVector: Vector3;

    /** The posterior direction of cup offset */
    apVector: Vector3;

    /** The original fitted cup-transform, in CT/world space */
    cupTransform: Matrix4;

    /** The original fitted cup-transform, in CT/world space */
    linerTransform: Matrix4;
};

export function computeFittedCup(state: PlannerState): FittedCup | null {
    try {
        if (!state.fittedComponents || !state.catalog || !state.template || !state.case) {
            return null;
        }
        const apiCup = findFittedCup(
            state.fittedComponents,
            state.catalog,
            state.template.cupUrl,
            computeLinerUrl(state),
            computeBearingUrl(state),
        );
        const globalCs = apiCup.global_cs;

        const result = {
            basis: {
                left:
                    state.case.operationalSide === Side.Left
                        ? vector3(globalCs.ml.vector)
                        : vector3(globalCs.ml.vector).negate(),
                posterior: vector3(globalCs.ap.vector),
                superior: vector3(globalCs.si.vector).negate(),
                position: vector3(globalCs.origin),
                lateral: vector3(globalCs.ml.vector),
                inferior: vector3(globalCs.si.vector),
            },
            nativeHjc: vector3(globalCs.origin),
            fittedHjc: vector3(apiCup.liner.head_centre),
            normal: vector3(apiCup.normal),
            siVector: vector3(apiCup.si_vector),
            apVector: vector3(apiCup.ap_vector),
            cupTransform: matrixFromApi(apiCup.tmatrix),
            linerTransform: matrixFromApi(apiCup.liner.tmatrix),
        };

        _logFittedCup(result);

        return result;
    } catch (error) {
        useAppErrorStore().handleError(error);
        return null;
    }
}

/**
 * Get the offset of the cup in world-space from api values
 */
export function cupWorldOffsetFromApi(
    fittedCup: FittedCup,
    templateOffset: AnatomicalOffset,
): Vector3 {
    return addVectors(
        multiplyScalar(fittedCup.basis.lateral, templateOffset.ml),
        multiplyScalar(fittedCup.basis.posterior, templateOffset.ap),
        multiplyScalar(fittedCup.basis.inferior, templateOffset.si),
    );
}

/**
 * Get the offset of the cup as used in the UI from api values
 */
export function acetabularCupOffsetFromApi(
    fittedCup: FittedCup,
    templateOffset: AnatomicalOffset,
): AcetabularCupOffset {
    const worldOffset = cupWorldOffsetFromApi(fittedCup, templateOffset);
    return {
        depth: worldOffset.dot(vector3(fittedCup.normal)),
        ap: -worldOffset.dot(vector3(fittedCup.apVector)),
        si: -worldOffset.dot(vector3(fittedCup.siVector)),
    };
}

export function alignmentCupOffsetFromApi(
    fittedCup: FittedCup,
    templateOffset: AnatomicalOffset,
): AlignmentCupOffset {
    const worldOffset = cupWorldOffsetFromApi(fittedCup, templateOffset);

    return {
        ml: worldOffset.dot(vector3(fittedCup.basis.lateral)),
        ap: worldOffset.dot(vector3(fittedCup.basis.posterior)),
        si: worldOffset.dot(vector3(fittedCup.basis.inferior)),
    };
}

export function cupWorldOffsetFromUIAcetabular(
    fittedCup: FittedCup,
    uiOffset: AcetabularCupOffset,
): Vector3 {
    return addVectors(
        multiplyScalar(fittedCup.normal, uiOffset.depth),
        multiplyScalar(fittedCup.apVector, -uiOffset.ap),
        multiplyScalar(fittedCup.siVector, -uiOffset.si),
    );
}

export function cupWorldOffsetFromUIAlignment(
    fittedCup: FittedCup,
    uiOffset: AlignmentCupOffset,
): Vector3 {
    return addVectors(
        multiplyScalar(fittedCup.basis.lateral, uiOffset.ml),
        multiplyScalar(fittedCup.basis.posterior, uiOffset.ap),
        multiplyScalar(fittedCup.basis.inferior, uiOffset.si),
    );
}

function _logFittedCup(fittedCup: FittedCup) {
    logValidation(
        joinIndented(2)([
            'Fitted-cup computed:',
            `native-hjc: ${formatVector(fittedCup.nativeHjc)}`,
            `fitted-hjc: ${formatVector(fittedCup.fittedHjc)}`,
            'basis:',
            ...indent(2)([
                `position: ${formatVector(fittedCup.basis.position)}`,
                `left: ${formatVector(fittedCup.basis.left)}`,
                `lateral: ${formatVector(fittedCup.basis.lateral)}`,
                `posterior: ${formatVector(fittedCup.basis.posterior)}`,
                `superior: ${formatVector(fittedCup.basis.superior)}`,
            ]),
            'axes:',
            ...indent(2)([
                `normal: ${formatVector(fittedCup.normal)}`,
                `si-vector: ${formatVector(fittedCup.siVector)}`,
                `ap-vector: ${formatVector(fittedCup.apVector)}`,
            ]),
            'cup-transform:',
            ...indent(2)(formatMatrixBasis(fittedCup.cupTransform, { precision: 5 })),
            'liner-transform:',
            ...indent(2)(formatMatrixBasis(fittedCup.linerTransform, { precision: 5 })),
        ]),
    );
}
